import { useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import {
  OrganizationType,
  PolicyholderType,
  type AdditionalInsuredInput,
} from "@covertree/new-business-graphql";
import { mapAdditionalInsuredRelationship } from "@covertree/utils";
import type { InsuredTabFormValues } from "@redux/slices/endorsement";

/** Personal Details section state and the add/remove additional-insured handlers. */
export const usePersonalDetailsAndAdditionalInsured = () => {
  const { control, watch, setValue } = useFormContext<InsuredTabFormValues>();

  const additionalInsured = watch("additionalInsured");

  const policyholderType = watch("type");
  const orgType = watch("orgType");
  const isOrganization = policyholderType === PolicyholderType.Organization;
  const showDescribeOrgType =
    isOrganization && orgType === OrganizationType.NotListed;

  const firstPersonName = watch("firstName");

  const additionalPersonDetails = useMemo(
    () =>
      additionalInsured
        ? `${additionalInsured.firstName} ${additionalInsured.lastName}, ${mapAdditionalInsuredRelationship(additionalInsured.relationship)}`
        : undefined,
    [additionalInsured]
  );

  const handleAdditionalInsuredFormSubmit = useCallback(
    (formData: AdditionalInsuredInput) => {
      setValue("additionalInsured", formData, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue]
  );

  const handleToggleAdditionalPersonSwitch = useCallback(() => {
    setValue("additionalInsured", null, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [setValue]);

  return {
    control,
    isOrganization,
    showDescribeOrgType,
    firstPersonName,
    additionalInsured,
    additionalPersonDetails,
    handleAdditionalInsuredFormSubmit,
    handleToggleAdditionalPersonSwitch,
  };
};
